import { useEffect, useRef, useState } from 'react';
import { getPaymentStatus } from '@/lib/api';

const MAX_POLL_ATTEMPTS = 6;
const POLL_INTERVAL_MS = 2200;
const TERMINAL_STATES = new Set(['paid', 'failed', 'expired']);

/**
 * Polls /api/payments/status/{sessionId} until the payment settles, reaches a
 * terminal state, or the polling attempt budget is exhausted. Returns a
 * `{ loading, status, payment, error }` shape suitable for UI rendering.
 */
export function usePaymentStatusPolling(sessionId) {
    const [state, setState] = useState({ loading: true, status: null, error: null, payment: null });
    const cancelledRef = useRef(false);
    const attemptsRef = useRef(0);
    const timeoutRef = useRef(null);

    useEffect(() => {
        cancelledRef.current = false;
        attemptsRef.current = 0;
        if (!sessionId) {
            setState({ loading: false, status: null, error: 'Missing session id', payment: null });
            return undefined;
        }
        const poll = async () => {
            try {
                const data = await getPaymentStatus(sessionId);
                if (cancelledRef.current) return;
                const ps = data.payment_status;
                if (TERMINAL_STATES.has(ps)) {
                    setState({ loading: false, status: ps, payment: data, error: null });
                    return;
                }
                attemptsRef.current += 1;
                if (attemptsRef.current >= MAX_POLL_ATTEMPTS) {
                    setState({ loading: false, status: ps || 'pending', payment: data, error: null });
                    return;
                }
                timeoutRef.current = setTimeout(poll, POLL_INTERVAL_MS);
            } catch (e) {
                if (cancelledRef.current) return;
                setState({ loading: false, status: null, error: 'Could not verify payment status.', payment: null });
            }
        };
        poll();
        return () => {
            cancelledRef.current = true;
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, [sessionId]);

    return state;
}
